import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, Form, Input, Modal, Result } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserCreateUserTotpDevice, useUserCreateUserTotpDeviceSecret } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { ModalSizes } from "../../../../common/constants";
import {
  AgentUserProfile,
  ClientUserProfile,
  CreateUserTotpDevice,
  CreateUserTotpDeviceSecret,
  UserTotpDeviceSecret,
} from "../../types";

interface Props {
  open: boolean;
  user: AgentUserProfile | ClientUserProfile;
  onFormCancel: () => void;
}

type FormState = "password" | "qr-code" | "name-and-totp-code";

export const CreateUserTotpDeviceForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [formState, setFormState] = useState<FormState>("password");
  const [totpDeviceSecret, setTotpDeviceSecret] = useState<UserTotpDeviceSecret>();

  const [form] = Form.useForm<CreateUserTotpDeviceSecret | CreateUserTotpDevice>();

  const createUserTotpDeviceSecret = useUserCreateUserTotpDeviceSecret();
  const createUserTotpDevice = useUserCreateUserTotpDevice();

  const handleOkClick = () => {
    form
      .validateFields()
      .then((values) => {
        switch (formState) {
          case "password":
            createUserTotpDeviceSecret.mutate(
              { id: user.id, object: values as CreateUserTotpDeviceSecret },
              {
                onSuccess: (secret) => {
                  setTotpDeviceSecret(secret);
                  setFormState("qr-code");
                  form.setFieldValue("secret", secret.secret);
                },
                onError: (error) => setErrorsToForm(form, "user.totpDevice.attrs", error.violations),
              },
            );
            break;
          case "qr-code":
            setFormState("name-and-totp-code");
            break;
          case "name-and-totp-code":
            createUserTotpDevice.mutate(
              { id: user.id, object: values as CreateUserTotpDevice },
              {
                onSuccess: handleFormCancel,
                onError: (error) => setErrorsToForm(form, "user.totpDevice.attrs", error.violations),
              },
            );
            break;
        }
      })
      .catch(resolveFormValidationError);
  };

  const handleFormCancel = () => {
    onFormCancel();
    setFormState("password");
    setTotpDeviceSecret(undefined);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("user.totpDevice.titles.create")}
      okText={formState === "name-and-totp-code" ? t("common.save") : t("common.next")}
      cancelText={t("common.cancel")}
      confirmLoading={createUserTotpDeviceSecret.isPending || createUserTotpDevice.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleOkClick}
      onCancel={handleFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="createUserTotpDeviceForm">
        {formState === "password" ? (
          <Form.Item name="password" label={t("user.totpDevice.attrs.password")} rules={[validations.notBlank]}>
            <Input.Password />
          </Form.Item>
        ) : (
          <HiddenInput name="password" />
        )}

        {formState === "password" && createUserTotpDeviceSecret.isPending && (
          <Result
            status="info"
            icon={<AntIcon type="loading" />}
            title={t("user.totpDevice.helpers.loadingQrCodeInfo")}
          />
        )}

        {formState === "qr-code" && totpDeviceSecret && (
          <>
            <HiddenInput name="secret" />

            <h2 style={{ marginBottom: 0, textAlign: "center" }}>{t("user.totpDevice.helpers.scanQrCodeInfo")}</h2>

            <img src={totpDeviceSecret.qrCodeDataUri} alt="qr-code" style={{ width: "100%" }} />

            <Alert
              type="info"
              className="center-align"
              title={<i>{t("user.totpDevice.helpers.copyManualSetupKeyInfo")}</i>}
              description={<b>{totpDeviceSecret.secret}</b>}
            />
          </>
        )}

        {formState === "name-and-totp-code" && (
          <>
            <HiddenInput name="secret" />

            <Form.Item
              name="name"
              label={t("user.totpDevice.attrs.name")}
              rules={[validations.notNull, validations.size(1, 64)]}
            >
              <Input placeholder={t("user.totpDevice.helpers.nameHint")} />
            </Form.Item>

            <Form.Item
              name="code"
              label={t("user.totpDevice.attrs.code")}
              rules={[validations.notBlank, validations.length(6), validations.numeric]}
            >
              <Input />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};
